import { Col, Button, Form } from "react-bootstrap";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setwizardPageNumber,
  setwizardPageName,
  setCheckPage,
} from "../../EntityAccountReducer";
import { notifyError, notifySuccess } from "MyComponents/Toaster";
import { updateEntityKycDraftStep, uploadEntityKycDraftFiles } from "../../../Utils/entityKycDraft";
import CountryDropdown from "MyComponents/UI/CountryDropdown";
import countriesStateData from "../../../data/states.json";
import countriesData from "../../../data/countries.json";
import MultiFileSelector from "./FilePicker";
import { YearPicker, MonthPicker, DayPicker } from "react-dropdown-date";

interface UploadedFile {
  filename: string;
  url: string;
}

const AuthorizedRepresentativeForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userIDFromRedux = useSelector((state: RootState) => state.entityAccount.userID);
  const { userId: userIdFromUrl } = useParams<{ userId?: string }>();
  const ssoUserID = userIDFromRedux || userIdFromUrl || "";
  const checkPage = useSelector((state: RootState) => state.entityAccount.checkPage);

  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  
  const [date, setDate] = useState({ year: "", month: "", day: "" });
  
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const [uploadedDocument, setUploadedDocument] = useState<UploadedFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Validations
  const [errors, setErrors] = useState<any>({});

  const authorizedRep = useSelector((state: RootState) => state.entityAccount.authorizedRepresentativeDetails);

  useEffect(() => {
    dispatch(setwizardPageName("authorizedRepresentatives"));
  }, [dispatch]);

  useEffect(() => {
    if (authorizedRep && authorizedRep.firstName) {
      setFirstName(authorizedRep.firstName || "");
      setMiddleName(authorizedRep.middleName || "");
      setLastName(authorizedRep.lastName || "");
      setEmail(authorizedRep.email || "");
      if (authorizedRep.dateOfBirth) {
        const [year, month, day] = authorizedRep.dateOfBirth.split("-");
        setDate({ year, month, day });
      }
      setStreetAddress(authorizedRep.streetAddress || "");
      setCity(authorizedRep.city || "");
      setSelectedState(authorizedRep.state || "");
      setPostalCode(authorizedRep.postalCode || "");
      setSelectedCountry(authorizedRep.country || "");
      if (authorizedRep.identityDocumentUrl) {
        setUploadedDocument([{ filename: 'Uploaded Document', url: authorizedRep.identityDocumentUrl }]);
      }
    }
  }, [authorizedRep]);

  const validateForm = () => {
    const newErrors: any = {};
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!email) newErrors.email = "Email is required";
    if (!date.year || !date.month || !date.day) newErrors.dob = "Date of Birth is required";
    if (!streetAddress) newErrors.streetAddress = "Street address is required";
    if (!city) newErrors.city = "City is required";
    if (!selectedState) newErrors.state = "State is required";
    if (!postalCode) newErrors.postalCode = "Postal code is required";
    if (!selectedCountry) newErrors.country = "Country is required";
    if (uploadedDocument.length === 0 && pendingFiles.length === 0) {
      newErrors.document = "Identity document is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCountryClick = (countryName: string) => {
    setSelectedCountry(countryName);
  };
  const handleStateClick = (stateName: string) => {
    setSelectedState(stateName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      let updatedDocuments = [...uploadedDocument];
      if (pendingFiles.length > 0) {
        const uploadedNewFiles = await uploadEntityKycDraftFiles(ssoUserID, pendingFiles);
        if (uploadedNewFiles) {
          updatedDocuments = [...updatedDocuments, ...uploadedNewFiles];
        }
      }

      const payload = [{
        firstName,
        middleName,
        lastName,
        email,
        dateOfBirth: date.year + "-" + date.month + "-" + date.day,
        streetAddress,
        city,
        state: selectedState,
        postalCode,
        country: selectedCountry,
        identityDocumentUrl: updatedDocuments[0]?.url || ""
      }];

      await updateEntityKycDraftStep(ssoUserID, "authorizedRepresentatives", payload);
      setIsSaved(true);
    } catch (error) {
      console.error(error);
      notifyError("Failed to update Authorized Representative");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (checkPage === 'SummaryPage') {
      dispatch(setCheckPage(''));
      dispatch(setwizardPageName("summary"));
      dispatch(setwizardPageNumber(7));
    } else {
      dispatch(setwizardPageName("beneficialOwners"));
      dispatch(setwizardPageNumber(6));
    }
  };

  return (
    <div className="pt-0 pb-3 mb-5 col-md-11 mx-auto px-2">
      <div className="row justify-content-center">
        <Col md={11}>
          <p className="fw-bold d-inline">{t('kyc.entity_account_app')}</p>
          <p className="text-warning d-inline ms-3 small">{t('common.in_progress')}</p>
          <h4 className="mt-3">Authorized Representative</h4>
          <p className="text-secondary small">
            Please provide the following information about the authorized representative.
          </p>
        </Col>
      </div>
      <div className="row justify-content-center mt-4">
        <Col md={11}>
          <Form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">First Name *</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} isInvalid={!!errors.firstName} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Middle Name</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="text" value={middleName} onChange={e => setMiddleName(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Last Name *</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="text" value={lastName} onChange={e => setLastName(e.target.value)} isInvalid={!!errors.lastName} />
                </Form.Group>
              </Col>
            </div>
            
            <div className="row mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Email Address *</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="email" value={email} onChange={e => setEmail(e.target.value)} isInvalid={!!errors.email} />
                </Form.Group>
              </Col>
            </div>

            <div className="row mb-3 mt-4">
              <Col md={12}>
                  <label className="form-label fw-bold">Date of Birth *</label>
                  <div className="d-flex align-items-center">
                      <div className="me-3">
                          <MonthPicker
                              defaultValue={'Month'}
                              numeric
                              short
                              caps
                              endYearGiven
                              year={date.year as any}
                              value={date.month as any}
                              onChange={(month: any) => setDate({ ...date, month: month })}
                              id={'month'}
                              name={'month'}
                              classes={'form-select rounded-5 py-3'}
                          />
                      </div>
                      <div className="me-3">
                          <DayPicker
                              defaultValue={'Date'}
                              year={date.year as any}
                              month={date.month as any}
                              endYearGiven
                              value={date.day as any}
                              onChange={(day: any) => setDate({ ...date, day: day })}
                              id={'day'}
                              name={'day'}
                              classes={'form-select rounded-5 py-3'}
                          />
                      </div>
                      <div>
                          <YearPicker
                              defaultValue={'Year'}
                              start={1900}
                              end={new Date().getFullYear()}
                              reverse
                              value={date.year as any}
                              onChange={(year: any) => setDate({ ...date, year: year })}
                              id={'year'}
                              name={'year'}
                              classes={'form-select rounded-5 py-3'}
                          />
                      </div>
                  </div>
                  {errors.dob && <small className="text-danger">{errors.dob}</small>}
              </Col>
            </div>

            <h5 className="mt-4">Address Details</h5>
            <div className="row mb-3 mt-3">
              <Col md={6}>
                <Form.Group>
                  <CountryDropdown
                    selectedCountry={selectedCountry}
                    handleCountryClick={handleCountryClick}
                    countryValidation={!!errors.country}
                    countriesData={countriesData}
                    title="Country *"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mt-4">
                  <Form.Label className="fw-bold">State / Province *</Form.Label>
                  <div className="dropdown">
                    <button className="btn w-100 bg-white border text-start d-flex justify-content-between align-items-center py-2 rounded-5" type="button" data-bs-toggle="dropdown">
                      {selectedState || "Select State"}
                      <i className="fas fa-chevron-down text-muted"></i>
                    </button>
                    <ul className="dropdown-menu w-100 shadow" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {countriesStateData.map((country: any) => 
                        country.name === selectedCountry && country.states?.map((state: any) => (
                          <li key={state.id} onClick={() => handleStateClick(state.name)}>
                            <span className="dropdown-item" style={{ cursor: 'pointer' }}>{state.name}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                  {errors.state && <small className="text-danger">{errors.state}</small>}
                </Form.Group>
              </Col>
            </div>
            
            <div className="row mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">City *</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="text" value={city} onChange={e => setCity(e.target.value)} isInvalid={!!errors.city} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Postal Code *</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} isInvalid={!!errors.postalCode} />
                </Form.Group>
              </Col>
            </div>

            <div className="row mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Street Address *</Form.Label>
                  <Form.Control className="rounded-5 py-2" type="text" value={streetAddress} onChange={e => setStreetAddress(e.target.value)} isInvalid={!!errors.streetAddress} />
                </Form.Group>
              </Col>
            </div>

            <h5 className="mt-4">Identity Document</h5>
            <div className="row mb-4 mt-3">
              <Col md={12}>
                <MultiFileSelector
                  maxFiles={1}
                  maxFileSizeMB={20}
                  existedFiles={uploadedDocument}
                  onFilesChange={(files: any) => setUploadedDocument(files)}
                  onPendingFilesChange={(files: any) => setPendingFiles(files)}
                />
                {errors.document && <small className="text-danger">{errors.document}</small>}
              </Col>
            </div>

            <div className="d-flex justify-content-start gap-3">
              <Button type="submit" className="btn btn-primary rounded-5" disabled={isLoading} style={{ minWidth: '200px' }}>
                {isLoading ? "Saving..." : "Save Authorized Representative"}
              </Button>
              {isSaved && (
                <Button className="btn btn-success rounded-5" onClick={handleNextStep} style={{ minWidth: '200px' }}>
                  Continue
                </Button>
              )}
            </div>
          </Form>
        </Col>
      </div>
    </div>
  );
};

export default AuthorizedRepresentativeForm;
